<template>
  <div class="unit-add-view">
    <template v-if="step === 'serial'">
      <form @submit.prevent="addUnit">
        <div class="pt-0h mx-0h">
          <h1 class="pb-0">{{ $t('unit.addUnit.header') }}</h1>
        </div>
        <TextInput
          ref="serialNumber"
          v-model="serialNumber"
          :label="$t('unit.addUnit.serialNumber')"
          :rules="serialNumberRules"
          class="unit-serial-number"
        />
        <TextInput
          ref="name"
          v-model="name"
          :label="$t('unit.addUnit.name')"
          class="unit-name"
        />
        <TextInput
          v-model="note"
          :label="$t('unit.addUnit.note')"
          class="unit-note mb-2"
        />
        <Btn
          button
          primary
          large
          block
          type="submit"
          :loading="saving"
          :disbled="saving"
        >
          {{ $t('unit.addUnit.button') }}
        </Btn>
      </form>
      <Btn outline block large class="mt-1 mb-1" @click="cancel">
        {{ $t('common.cancel') }}
      </Btn>
    </template>

    <UnitAddPairingCodeStep
      v-else-if="step === 'pairingCode'"
      v-model:pairing-code="pairingCode"
      :serial-number="serialNumber"
      :show-error="showPinError"
      :remaining-attempts="remainingAttempts"
      :loading="saving"
      @change-serial="goToStep('serial')"
      @submit="submitPairingCode"
      @problem-with-code="contactSupport"
    />

    <UnitAddLockedStep
      v-else-if="step === 'locked'"
      :countdown="lockCountdown"
      @contact-support="contactSupport"
      @back-to-list="cancel"
    />
  </div>
</template>

<script>
import TextInput from '@acs/components-library/src/components/common/Input.vue';
import Btn from '@acs/components-library/src/components/common/Btn.vue';
import UnitAddPairingCodeStep from '@acs/components-library/src/components/unitAdd/UnitAddPairingCodeStep.vue';
import UnitAddLockedStep from '@acs/components-library/src/components/unitAdd/UnitAddLockedStep.vue';
import { PairUnitMutation, addUnitToCache } from '@acs/components-library/src/gql/live/UnitAddModal.js';
import { appLabelConfig } from '@acs/components-library/src/appLabel/appLabelConfig.js';
import {
  formatRetryAfterCountdown,
  isValidPairingCode,
  isValidSerialNumber,
  normalizePairingCode,
  normalizeSerialNumber,
} from '@acs/components-library/src/helpers/unitPairingCode.js';
import { useToastStore } from '../store/toastStore.js';

const { VITE_HELP_EMAIL } = import.meta.env;

export default {
  components: {
    TextInput,
    Btn,
    UnitAddPairingCodeStep,
    UnitAddLockedStep,
  },
  emits: ['onCompleted', 'onCancel'],
  setup() {
    const toastStore = useToastStore();
    return { toastStore };
  },
  data() {
    return {
      step: 'serial',
      serialNumber: '',
      note: '',
      name: 'My new unit',
      pairingCode: '',
      showPinError: false,
      remainingAttempts: 0,
      lockCountdown: '00:00',
      retryAfterSeconds: 0,
      lockTimerId: null,
      saving: false,
      serialNumberRules: [
        (v) => !!normalizeSerialNumber(v) || this.$t('unit.addUnit.snRequired'),
        (v) => isValidSerialNumber(v) || this.$t('unit.addUnit.snLengthRange'),
      ],
    };
  },
  beforeUnmount() {
    this.clearLockTimer();
  },
  methods: {
    goToStep(step) {
      if (step !== 'locked') {
        this.clearLockTimer();
      }
      this.step = step;
    },
    resetFlow() {
      this.clearLockTimer();
      this.step = 'serial';
      this.pairingCode = '';
      this.showPinError = false;
      this.remainingAttempts = 0;
      this.retryAfterSeconds = 0;
      this.lockCountdown = '00:00';
    },
    cancel() {
      this.resetFlow();
      this.$emit('onCancel');
    },
    contactSupport() {
      if (VITE_HELP_EMAIL) {
        window.open(`mailto:${VITE_HELP_EMAIL}`, '_blank');
      }
    },
    clearLockTimer() {
      if (this.lockTimerId) {
        clearInterval(this.lockTimerId);
        this.lockTimerId = null;
      }
    },
    startLockCountdown(retryAfter) {
      this.clearLockTimer();
      this.retryAfterSeconds = Math.max(0, Math.floor(Number(retryAfter) || 0));
      this.lockCountdown = formatRetryAfterCountdown(this.retryAfterSeconds);
      this.goToStep('locked');

      this.lockTimerId = setInterval(() => {
        this.retryAfterSeconds = Math.max(0, this.retryAfterSeconds - 1);
        this.lockCountdown = formatRetryAfterCountdown(this.retryAfterSeconds);
        if (this.retryAfterSeconds <= 0) {
          this.clearLockTimer();
          this.showPinError = false;
          this.pairingCode = '';
          this.goToStep('pairingCode');
        }
      }, 1000);
    },
    async pairUnitRequest({ includePairingCode }) {
      const pairing = {
        serialNumber: normalizeSerialNumber(this.serialNumber),
        name: this.name,
        note: this.note,
      };

      if (includePairingCode) {
        pairing.pairingCode = normalizePairingCode(this.pairingCode);
      }

      this.saving = true;
      try {
        const { data: { pairUnit } } = await this.$apollo.mutate({
          mutation: PairUnitMutation,
          variables: { pairing },
        });
        return pairUnit;
      } finally {
        this.saving = false;
      }
    },
    handlePairUnitResult(pairUnit, { codeWasSubmitted }) {
      if (!pairUnit) return;

      if (pairUnit.__typename === 'InvalidPairingCodeError') {
        this.remainingAttempts = pairUnit.remainingAttempts;
        this.showPinError = Boolean(codeWasSubmitted) || this.step === 'pairingCode';
        this.goToStep('pairingCode');
        return;
      }

      if (pairUnit.__typename === 'PairingCodeAttemptsExceededError') {
        this.startLockCountdown(pairUnit.retryAfter);
        return;
      }

      if (pairUnit.__typename === 'UnitAlreadyPairedError') {
        this.toastStore.showWarning(this.$t('unit.addUnit.alreadyPaired'));
        return;
      }

      if (pairUnit.__typename === 'UnitNotFoundError') {
        this.toastStore.showError(this.$t('unit.unitNotFound'));
        return;
      }

      if (pairUnit.__typename === 'UnitAccessDeniedError') {
        this.toastStore.showError(this.$t('unit.addUnit.accessDenied'));
        return;
      }

      if (pairUnit.__typename === 'Unit' || pairUnit.__typename === 'UnitNeverConnected') {
        const { unitsPerPage } = appLabelConfig.poolLiveFront.unitList;
        addUnitToCache(pairUnit, { unitsPerPage });
        this.toastStore.showSuccess(this.$t('unit.addUnit.added'));
        this.resetFlow();
        this.$emit('onCompleted');
      }
    },
    async addUnit() {
      const serialNumberInput = this.$refs.serialNumber;
      this.serialNumber = normalizeSerialNumber(this.serialNumber);

      if (!isValidSerialNumber(this.serialNumber) || serialNumberInput.hasError) {
        serialNumberInput.$refs.input.focus();
        return;
      }

      this.showPinError = false;
      // Try pairing directly from the serial step. Units that require
      // a pairing code respond with InvalidPairingCodeError, which moves
      // the flow to the pairing-code step via handlePairUnitResult.
      const includePairingCode = isValidPairingCode(this.pairingCode);
      const pairUnit = await this.pairUnitRequest({ includePairingCode });
      this.handlePairUnitResult(pairUnit, { codeWasSubmitted: includePairingCode });
    },
    async submitPairingCode() {
      const includePairingCode = isValidPairingCode(this.pairingCode);
      const pairUnit = await this.pairUnitRequest({ includePairingCode });
      this.handlePairUnitResult(pairUnit, { codeWasSubmitted: includePairingCode });
    },
  },
};
</script>

<style scoped lang="scss">

// Fix input button QR code icon
.input-group .append button {
  display: flex;
  padding: 0 0.5rem;
  color: var(--textColor);
}
</style>
